/**
 * Pull Engine — Server → Client sync.
 *
 * Fetches GET /sync/pull?since=<iso>&cursor=<token> in a loop until the server
 * stops returning a `next_cursor`, then writes every entity into Dexie with
 * one rule: rows that are locally `sync_status === "pending"` are skipped, so
 * an offline edit waiting in the push queue is never clobbered by an older
 * server snapshot.
 *
 * Failure model
 *   • Per-page transport errors retry up to PULL_MAX_RETRIES with 2/4/8s backoff.
 *   • Any unrecoverable error is logged via the PHI-safe logger and swallowed
 *     — runPullCycle resolves with ok:false so the UI can fall through to a
 *     stale-cache read instead of crashing.
 *
 * Checkpointing
 *   • localStorage[PULL_LAST_SYNC_KEY] is written after each successful page,
 *     so an interrupted multi-page sync resumes from the latest applied page
 *     instead of restarting from scratch. The checkpoint is the server's own
 *     `timestamp` field — never `Date.now()` (clock skew between client and
 *     server would otherwise miss or duplicate records).
 */

import {
  API_BASE_URL,
  BOOTSTRAP_COMPLETED_AT_KEY,
  IS_DEV,
  PULL_ENDPOINT,
  PULL_LAST_SYNC_KEY,
  PULL_LAST_SYNCED_AT_KEY,
  PULL_MAX_RETRIES,
  PULL_PAGE_LIMIT,
  SYNC_FETCH_TIMEOUT_MS,
} from "../constants";
import { db } from "../db/schema";
import type {
  AllergyRecord,
  AppointmentRecord,
  EntityType,
  PatientRecord,
  PrescriptionRecord,
  SyncFields,
  VisitRecord,
  VitalsRecord,
} from "../db/db.types";
import { wrapForStorage } from "../db/encryption-bridge";
import { apiFetch } from "../services/api.client";
import {
  assertClinicScopeUnchanged,
  ClinicScopeChangedError,
  getClinicScopeToken,
  isClinicScopeStale,
} from "../utils/active-clinic";
import { logger } from "../utils/logger";

// ─── Types ───────────────────────────────────────────────────────────────────

/** Server-side soft-delete flag attached to each entity row. */
type Tombstoned<T> = T & { _deleted?: boolean };

interface PullChanges {
  patients?:      Tombstoned<PatientRecord>[];
  visits?:        Tombstoned<VisitRecord>[];
  vitals?:        Tombstoned<VitalsRecord>[];
  prescriptions?: Tombstoned<PrescriptionRecord>[];
  appointments?:  Tombstoned<AppointmentRecord>[];
  allergies?:     Tombstoned<AllergyRecord>[];
}

interface PullResponse {
  /** ISO server timestamp — becomes the next `since` parameter. */
  timestamp: string;
  changes: PullChanges;
  /** If non-null, fetch another page using this opaque cursor. */
  next_cursor?: string | null;
  /** Legacy fallback when the server doesn't paginate. */
  has_more?: boolean;
  /**
   * Bootstrap only: set by a server that cuts pages short by bytes. Until a
   * page carries it, bootstrap must not ask for large pages.
   */
  page_byte_budget?: number | null;
}

export interface PullCycleResult {
  ok: boolean;
  pages: number;
  counts: Record<keyof PullChanges, number>;
  /** Set when ok=false — for diagnostics, not user-visible. */
  reason?: "offline" | "transport" | "auth" | "server" | "unknown" | "stale_tab";
}

// ─── Sync-snapshot event bus (consumed by usePullStatus hook) ────────────────

export type PullState = "idle" | "bootstrapping" | "syncing" | "synced" | "offline" | "error";

export interface PullSnapshot {
  state: PullState;
  /** Pages applied so far in the current/last cycle. */
  pagesDone: number;
  /** Total records (across all entities) applied so far in the current/last cycle. */
  recordsApplied: number;
  /** Wall-clock ISO time of the most recent successful cycle, or null. */
  lastSyncedAt: string | null;
}

type Listener = (snap: PullSnapshot) => void;
const listeners = new Set<Listener>();

let snapshot: PullSnapshot = {
  state: navigator.onLine ? "idle" : "offline",
  pagesDone: 0,
  recordsApplied: 0,
  lastSyncedAt: localStorage.getItem(PULL_LAST_SYNCED_AT_KEY),
};

export function subscribePullState(fn: Listener): () => void {
  listeners.add(fn);
  fn(snapshot);
  return () => { listeners.delete(fn); };
}

export function getPullSnapshot(): PullSnapshot { return snapshot; }

/** Back-compat helper for callers that only care about the state field. */
export function getPullState(): PullState { return snapshot.state; }

function emit(patch: Partial<PullSnapshot>): void {
  const next = { ...snapshot, ...patch };
  if (
    next.state          === snapshot.state &&
    next.pagesDone      === snapshot.pagesDone &&
    next.recordsApplied === snapshot.recordsApplied &&
    next.lastSyncedAt   === snapshot.lastSyncedAt
  ) return;
  snapshot = next;
  listeners.forEach(l => { try { l(snapshot); } catch { /* listener errors must not break sync */ } });
}

function setState(state: PullState): void { emit({ state }); }

// ─── Public entry point ──────────────────────────────────────────────────────

let inFlight: Promise<PullCycleResult> | null = null;

// Set to true by markCheckpointStale() (called from clearPhiTables on signOut).
// Any in-flight pull that started before signOut must NOT overwrite the
// freshly-reset checkpoint — that would undo the 30-day lookback window.
// Cleared at the start of each new pull cycle so post-signIn pulls write normally.
let checkpointStale = false;

/**
 * Called by clearPhiTables() on signOut so any in-flight pull cycle that
 * completes after the wipe doesn't overwrite the reset checkpoint.
 */
export function markCheckpointStale(): void {
  checkpointStale = true;
}

/**
 * Run one full pull cycle: fetch every page newer than the last checkpoint,
 * apply them to Dexie, advance the checkpoint after each page.
 *
 * Concurrent calls coalesce — the second caller awaits the first.
 */
export function runPullCycle(): Promise<PullCycleResult> {
  if (inFlight) return inFlight;
  inFlight = doRunPullCycle().finally(() => { inFlight = null; });
  return inFlight;
}

async function doRunPullCycle(): Promise<PullCycleResult> {
  // New cycle starts — accept checkpoint writes again.
  checkpointStale = false;

  const counts: Record<keyof PullChanges, number> = {
    patients: 0, visits: 0, vitals: 0, prescriptions: 0, appointments: 0, allergies: 0,
  };

  if (!navigator.onLine) {
    emit({ state: "offline", pagesDone: 0, recordsApplied: 0 });
    return { ok: false, pages: 0, counts, reason: "offline" };
  }

  // Another tab moved this browser to a different clinic; this tab must not
  // pull its old clinic's rows into the cache that now belongs to the new one.
  if (isClinicScopeStale()) {
    return { ok: false, pages: 0, counts, reason: "stale_tab" };
  }
  const scopeToken = getClinicScopeToken();

  // Bootstrap is the only path that writes the very first PULL_LAST_SYNC_KEY.
  // If it's absent the delta loop has no point of reference and must not run;
  // sync-manager is responsible for invoking the bootstrap engine first.
  if (!localStorage.getItem(BOOTSTRAP_COMPLETED_AT_KEY)) {
    if (IS_DEV) logger.info("[pull] skipped — bootstrap has not completed yet");
    return { ok: false, pages: 0, counts, reason: "server" };
  }

  // Fresh cycle: zero out progress so the UI shows "Syncing… 0 records" not stale numbers.
  emit({ state: "syncing", pagesDone: 0, recordsApplied: 0 });

  let cursor: string | null = null;
  let pages = 0;

  try {
    do {
      const since = getSinceParam();
      if (since === null) {
        logger.warn("[pull] aborted mid-cycle — no checkpoint", { pages });
        setState("error");
        return { ok: false, pages, counts, reason: "server" };
      }
      if (IS_DEV) {
        logger.info(`[pull] page start since=${since} cursor=${cursor ?? "null"}`);
      }
      // Explicit annotation: CRA's TS check can't infer through fetchPageWithRetry's
      // while(true) flow, even though the function declares its return type.
      const page: PullResponse | null = await fetchPageWithRetry(since, cursor);
      if (!page) {
        // fetchPageWithRetry exhausted retries; bail out without advancing
        // the checkpoint so the next cycle re-fetches from the same point.
        setState("error");
        return { ok: false, pages, counts, reason: "transport" };
      }

      await applyChanges(page.changes, counts, scopeToken);
      // Per work-items spec: write checkpoint after each successful page.
      writeCheckpoint(page.timestamp);
      pages += 1;

      const recordsApplied =
        counts.patients + counts.visits + counts.vitals +
        counts.prescriptions + counts.appointments + counts.allergies;
      emit({ pagesDone: pages, recordsApplied });

      // Legacy servers that return has_more without next_cursor: one retry is
      // acceptable (the server may produce a cursor on the next call) but an
      // infinite loop is not. Break if we've seen __legacy_continue__ twice in
      // a row by checking whether next_cursor is still absent on the new page.
      if (page.next_cursor) {
        cursor = page.next_cursor;
      } else if (page.has_more) {
        if (cursor === "__legacy_continue__") {
          logger.warn("[pull] has_more=true but no next_cursor on consecutive pages — stopping");
          cursor = null;
        } else {
          cursor = "__legacy_continue__";
        }
      } else {
        cursor = null;
      }
    } while (cursor);

    if (IS_DEV) {
      logger.info(
        `[pull] cycle ok pages=${pages}`,
        {
          patients: counts.patients,
          visits: counts.visits,
          prescriptions: counts.prescriptions,
          appointments: counts.appointments,
          allergies: counts.allergies,
          vitals: counts.vitals,
        },
      );
    }

    const lastSyncedAt = new Date().toISOString();
    localStorage.setItem(PULL_LAST_SYNCED_AT_KEY, lastSyncedAt);
    emit({ state: "synced", lastSyncedAt });
    return { ok: true, pages, counts };
  } catch (err) {
    if (err instanceof ClinicScopeChangedError) {
      return { ok: false, pages, counts, reason: "stale_tab" };
    }
    // Last-line catch — applyChanges or unexpected throws. Never re-raise:
    // a pull failure must not crash the app or surface to the doctor.
    const name = err instanceof Error ? err.name : "Unknown";
    logger.warn("[pull] cycle failed", { error_name: name, pages });
    setState("error");
    return { ok: false, pages, counts, reason: "unknown" };
  }
}

// ─── Checkpoint helpers ──────────────────────────────────────────────────────

function getSinceParam(): string | null {
  return localStorage.getItem(PULL_LAST_SYNC_KEY);
}

function writeCheckpoint(serverTimestamp: string): void {
  // If clearPhiTables reset the checkpoint after this pull cycle started,
  // discard our result — writing it would overwrite the 30-day lookback.
  // The checkpoint lives in shared localStorage, so a reset by ANOTHER tab's
  // clinic switch must win too.
  if (checkpointStale || isClinicScopeStale()) return;
  // Trust the server's clock — using Date.now() here would miss records
  // written between the server's response and our local write.
  localStorage.setItem(PULL_LAST_SYNC_KEY, serverTimestamp);
}

// ─── HTTP layer (with retry/backoff) ─────────────────────────────────────────

class PullHttpError extends Error {
  constructor(readonly status: number) { super(`HTTP ${status}`); }
}

async function fetchPageWithRetry(
  since: string,
  cursor: string | null,
): Promise<PullResponse | null> {
  let attempt = 0;
  // Loop bound: PULL_MAX_RETRIES retries after the initial attempt = total attempts.
  // We treat PULL_MAX_RETRIES as the *retry count* (3 retries → 4 attempts max),
  // matching the spec's "retried up to 3 times with 2s, 4s, 8s" wording.
  while (true) {
    try {
      return await fetchPage(since, cursor);
    } catch (err) {
      // Not a transient failure: this tab stays stale until it is reloaded.
      if (err instanceof ClinicScopeChangedError) throw err;
      // 4xx (except 408/429) is a contract bug — don't retry.
      if (err instanceof PullHttpError && err.status >= 400 && err.status < 500
          && err.status !== 408 && err.status !== 429) {
        logger.warn("[pull] non-retryable HTTP", { status: err.status });
        return null;
      }
      attempt += 1;
      if (attempt > PULL_MAX_RETRIES) {
        const status = err instanceof PullHttpError ? err.status : 0;
        logger.warn("[pull] retries exhausted", { status, attempts: attempt - 1 });
        return null;
      }
      // Exponential backoff: 2^attempt seconds → 2s, 4s, 8s.
      const delayMs = (2 ** attempt) * 1000;
      logger.warn("[pull] transient failure — backing off", { delayMs, attempt });
      await sleep(delayMs);
      // Bail early if we went offline mid-retry.
      if (!navigator.onLine) {
        setState("offline");
        return null;
      }
    }
  }
}

async function fetchPage(since: string, cursor: string | null): Promise<PullResponse> {
  const params = new URLSearchParams({
    since,
    limit: String(PULL_PAGE_LIMIT),
  });
  if (cursor && cursor !== "__legacy_continue__") params.set("cursor", cursor);

  const res = await apiFetch(`${API_BASE_URL}${PULL_ENDPOINT}?${params.toString()}`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
    signal: AbortSignal.timeout(SYNC_FETCH_TIMEOUT_MS),
  });

  if (!res.ok) throw new PullHttpError(res.status);

  const json = await res.json();
  // Tolerate both raw and BaseAPIResponse-wrapped envelopes.
  const data = (json && typeof json === "object" && "success" in json && "data" in json)
    ? json.data
    : json;

  if (!data || typeof data.timestamp !== "string" || typeof data.changes !== "object") {
    throw new Error("Malformed pull response");
  }
  return data as PullResponse;
}

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

// ─── Apply changes to Dexie ──────────────────────────────────────────────────
//
// `applyChanges` and the supporting helpers are exported so the bootstrap
// engine can write its initial-sync rows through exactly the same Dexie
// write path. Both engines must obey the same rules (atomic per-page
// transaction, sync_status=pending guard, tombstone semantics), so sharing
// the implementation is the only way to keep them in step.

const ENTITY_PRIMARY_KEYS: Record<keyof PullChanges, string> = {
  patients:      "patient_id",
  visits:        "visit_id",
  vitals:        "vital_id",
  prescriptions: "prescription_id",
  appointments:  "appointment_id",
  allergies:     "allergy_id",
};

// A pre-encrypted row ready to write inside a Dexie transaction.
// envelope===null means the server tombstoned this row (soft-delete).
type ReadyRow = { pk: string; envelope: Record<string, unknown> | null };

async function prepareRows<T extends SyncFields>(
  entityType: EntityType,
  rows: Tombstoned<T>[] | undefined,
  pkField: string,
): Promise<ReadyRow[]> {
  if (!rows || rows.length === 0) return [];
  const prepared = await Promise.all(rows.map(async row => {
    const pk = (row as unknown as Record<string, string>)[pkField] ?? "";
    if (!pk) return null;
    if (row._deleted) return { pk, envelope: null };
    const { _deleted: _ignored, ...clean } = row;
    const stamped: Record<string, unknown> = {
      ...(clean as Record<string, unknown>),
      sync_status:      "synced",
      version:          (clean as Record<string, unknown>).version ?? 1,
      local_updated_at: new Date().toISOString(),
    };
    const envelope = await wrapForStorage(entityType, stamped);
    return { pk, envelope };
  }));
  return prepared.filter((r): r is ReadyRow => r !== null);
}

export type { PullChanges, PullResponse };
export type ServerRowSet<T> = Tombstoned<T>;

/** Live state-bus emitter exposed for the bootstrap engine. */
export function notifySyncState(patch: Partial<PullSnapshot>): void {
  emit(patch);
}

export async function applyChanges(
  changes: PullChanges,
  counts: Record<keyof PullChanges, number>,
  /** From getClinicScopeToken() when the sync run started. */
  scopeToken: string | null,
): Promise<void> {
  // Encrypt all incoming rows BEFORE opening the Dexie transaction — Web Crypto
  // promises don't participate in Dexie's transaction lifecycle.
  const [patients, visits, vitals, prescriptions, appointments, allergies] =
    await Promise.all([
      prepareRows("patient",      changes.patients,      ENTITY_PRIMARY_KEYS.patients),
      prepareRows("visit",        changes.visits,        ENTITY_PRIMARY_KEYS.visits),
      prepareRows("vitals",       changes.vitals,        ENTITY_PRIMARY_KEYS.vitals),
      prepareRows("prescription", changes.prescriptions, ENTITY_PRIMARY_KEYS.prescriptions),
      prepareRows("appointment",  changes.appointments,  ENTITY_PRIMARY_KEYS.appointments),
      prepareRows("allergy",      changes.allergies,     ENTITY_PRIMARY_KEYS.allergies),
    ]);

  // One transaction across every table so a crash mid-apply rolls back the
  // whole page, leaving the checkpoint un-advanced and the sync replayable.
  await db.transaction(
    "rw",
    [db.patients, db.visits, db.vitals, db.prescriptions, db.appointments, db.allergies],
    async () => {
      // Checked inside the transaction, not before it: a page fetched just
      // before a switch (in this tab or another) belongs to the old clinic,
      // and the encryption above leaves time for that switch to wipe the
      // cache. Throwing here rolls the page back.
      assertClinicScopeUnchanged(scopeToken);
      await applyTable(db.patients,      patients,      "patients",      counts);
      await applyTable(db.visits,        visits,        "visits",        counts);
      await applyTable(db.vitals,        vitals,        "vitals",        counts);
      await applyTable(db.prescriptions, prescriptions, "prescriptions", counts);
      await applyTable(db.appointments,  appointments,  "appointments",  counts);
      await applyTable(db.allergies,     allergies,     "allergies",     counts);
    },
  );
}

async function applyTable(
  // Widened to `any` table type: each table's OnDisk type has a distinct status
  // union, so a precise union of all six table types intersects to `never` on put().
  table: {
    bulkGet:    (keys: string[]) => Promise<({ sync_status?: string } | undefined)[]>;
    bulkPut:    (rows: never[]) => Promise<unknown>;
    bulkDelete: (keys: string[]) => Promise<unknown>;
  },
  rows: ReadyRow[],
  entityKey: keyof PullChanges,
  counts: Record<keyof PullChanges, number>,
): Promise<void> {
  if (rows.length === 0) return;
  // Bulk reads and writes: one IndexedDB request per table instead of one per
  // row. A full bootstrap page is up to 500 rows in each of six tables.
  const existingRows = await table.bulkGet(rows.map(r => r.pk));
  const puts: Record<string, unknown>[] = [];
  const deletes: string[] = [];
  rows.forEach((row, i) => {
    // Hard rule: never overwrite a row that has unsynced local changes.
    if (existingRows[i]?.sync_status === "pending") return;
    if (row.envelope === null) deletes.push(row.pk);
    else puts.push(row.envelope);
  });
  if (puts.length > 0) await table.bulkPut(puts as never[]);
  if (deletes.length > 0) await table.bulkDelete(deletes);
  counts[entityKey] += puts.length + deletes.length;
}

// ─── Test utility (not for production code paths) ───────────────────────────

/**
 * Reset both sync flags so the next app load re-runs bootstrap from scratch
 * and re-builds the delta watermark from that bootstrap's snapshot timestamp.
 * Clearing only one of the two would leave the engines in an inconsistent
 * state — pull would refuse to run (no bootstrap flag) while bootstrap would
 * be triggered (no flag), so always clear them together.
 */
export function resetPullCheckpoint(): void {
  localStorage.removeItem(PULL_LAST_SYNC_KEY);
  localStorage.removeItem(BOOTSTRAP_COMPLETED_AT_KEY);
}
